'use client';

import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';

export function ResaleValue() {
  const { locale } = useLocale();

  return (
    <section className="relative py-14 md:py-28 border-t border-white/5 bg-gradient-to-b from-ink-900 to-ink-800/50">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-soft font-semibold mb-4">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {t(dict.resale.eyebrow, locale)}
          </div>
          <h2 className="font-serif text-display-sm text-cream leading-[0.98] mb-6">
            {t(dict.resale.heading, locale)}
          </h2>
          <p className="text-fog-300 text-lg leading-relaxed">
            {t(dict.resale.body, locale)}
          </p>
        </div>

        <div className="rounded-luxe bg-ink-800/80 border border-white/10 p-6 shadow-card">
          <div className="text-xs tracking-[0.2em] uppercase text-fog-500 font-semibold mb-4">
            {t(dict.resale.exampleLabel, locale)}
          </div>
          <div className="space-y-3.5">
            {dict.resale.examples.map((ex, i) => {
              const pctFill = Math.min(ex.retention, 100);
              const tone = ex.retention >= 80 ? 'gold' : ex.retention >= 60 ? 'violet' : 'muted';
              const barClass =
                tone === 'gold'
                  ? 'bg-gradient-to-r from-gold-deep to-gold-soft'
                  : tone === 'violet'
                  ? 'bg-gradient-to-r from-cta-from to-cta-to'
                  : 'bg-fog-700';
              return (
                <motion.div
                  key={ex.brand + ex.model}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-center gap-4"
                >
                  <div className="w-36 shrink-0">
                    <div className="text-[10px] tracking-widest uppercase text-fog-500">
                      {ex.brand}
                    </div>
                    <div className="text-sm text-fog-100 font-medium">{ex.model}</div>
                  </div>
                  <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${pctFill}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1, delay: i * 0.1, ease: 'easeOut' }}
                      className={`h-full ${barClass}`}
                    />
                  </div>
                  <div
                    className={`w-16 text-end text-sm font-mono font-semibold ${
                      tone === 'gold'
                        ? 'text-gold-soft'
                        : tone === 'violet'
                        ? 'text-violet-300'
                        : 'text-fog-500'
                    }`}
                  >
                    {ex.retention}%
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
